import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowRight, Eye, EyeOff, LockKeyhole, UserRound, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

type AuthMode = 'login' | 'register';

export function AuthModal({ initialMode = 'login', onClose, required = false }: { initialMode?: AuthMode; onClose: () => void; required?: boolean }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const firstInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInput.current?.focus();
    const handleKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !required) onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, mode, required]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'login') await login(username, password);
      else await register(username, displayName, password);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '操作失败');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={`modal-backdrop ${required ? 'required-auth-backdrop' : ''}`} role="presentation" onMouseDown={(event) => { if (!required && event.target === event.currentTarget) onClose(); }}>
      <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        {!required && <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="关闭"><X size={20} /></button>}
        <div className="auth-mark" aria-hidden="true"><span /><span /><span /><span /></div>
        <p className="eyebrow">{required ? '先登录，再开始做图' : '账号与本地图纸'}</p>
        <h2 id="auth-title">{mode === 'login' ? '登录账号' : '注册一个账号'}</h2>
        <p className="auth-intro">服务器只记录账号和会员状态。原图、图纸和施工进度仍保存在这台设备上。</p>

        <div className="auth-tabs" role="tablist" aria-label="账号操作">
          <button className={mode === 'login' ? 'active' : ''} type="button" onClick={() => { setMode('login'); setError(''); }}>登录</button>
          <button className={mode === 'register' ? 'active' : ''} type="button" onClick={() => { setMode('register'); setError(''); }}>注册</button>
        </div>

        <form onSubmit={submit} className="auth-form">
          <label htmlFor="auth-username">用户名</label>
          <div className="input-shell">
            <UserRound size={18} />
            <input ref={firstInput} id="auth-username" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="3～24 位中文、字母或数字" required />
          </div>
          {mode === 'register' && (
            <>
              <label htmlFor="auth-display-name">昵称</label>
              <div className="input-shell">
                <UserRound size={18} />
                <input id="auth-display-name" autoComplete="nickname" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="大家会看到的名字" required />
              </div>
            </>
          )}
          <label htmlFor="auth-password">密码</label>
          <div className="input-shell">
            <LockKeyhole size={18} />
            <input id="auth-password" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 8 位" minLength={8} required />
            <button className="input-action" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? '隐藏密码' : '显示密码'}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button auth-submit" type="submit" disabled={submitting}>
            <span>{submitting ? '正在处理…' : mode === 'login' ? '登录' : '注册并登录'}</span>
            {!submitting && <ArrowRight size={18} />}
          </button>
        </form>
        <p className="auth-footnote">广场可直接浏览；绘制、导入、点赞和收藏需要登录。你的原图与进度仍只保存在当前设备。</p>
      </section>
    </div>
  );
}
